import { Button } from "@/components/ui/button";
import { Calendar, Download, Globe, Mail, MapPin, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import logo from "../assets/logo.svg";
import slogan from "../assets/slogan-white.svg";
import { useEffect, useState } from "react";
import { PopularCategory } from "@/types/home";
import { categoriesApi } from "@/services/home";
import { useLanguage } from "@/hooks/useLanguage";
import { Category } from "@/types/api";
import dataService from "@/services/dataService";

const Footer = () => {
  const { isRTL } = useLanguage();

  // API DATA STATES
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // FETCH CATEGORIES FROM JSON SERVER (LIMITED TO 6)
  useEffect(() => {
    const fetchCategories = async (): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);

        // Use dataService to get popular categories
        const response = await dataService.getCategories(1, 12, {
          popular: true,
        });

        // Limit to 6 categories using React methods (slice)
        const limitedCategories = response?.data?.data?.slice(0, 6);
        setCategories(limitedCategories);
      } catch (err) {
        console.error("Failed to fetch categories:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load categories";
        setError(errorMessage);
        setCategories([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, []);

  return (
    <footer
      className="bg-ecosafe-black text-white relative overflow-hidden"
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12 sm:py-16 relative z-10">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8 lg:gap-12">
          {/* Enhanced Company Branding Section */}
          <div className="lg:col-span-1">
            <div className="flex flex-col items-center lg:items-start">
              {/* Logo and Slogan Container with Better Layout */}
              <div className="mb-8 w-full max-w-xs mx-auto lg:mx-0">
                {/* Logo Section */}
                <Link
                  to="/"
                  className="block group"
                  aria-label={
                    isRTL
                      ? "الصفحة الرئيسية - إيكوسيف للتدريب والاستشارات"
                      : "Home - ECOSAFE Training and Consultancy"
                  }
                >
                  <div className="relative flex justify-center lg:justify-start">
                    <img
                      src={logo}
                      width={595}
                      height={260}
                      alt={
                        isRTL
                          ? "إيكوسيف للتدريب والاستشارات"
                          : "ECOSAFE Training and Consultancy"
                      }
                      className="w-48 sm:w-52 md:w-56 lg:w-60 xl:w-64 h-auto object-contain hover:scale-105 transition-all duration-300 drop-shadow-2xl filter brightness-110"
                    />
                    {/* Enhanced glow effect */}
                    <div className="absolute inset-0 bg-ecosafe-green/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-2xl -z-10"></div>
                  </div>
                </Link>

                {/* Slogan Section with Refined Styling */}
                <div className="relative group">
                  <div className="flex justify-center lg:justify-start">
                    <img
                      src={slogan}
                      width={595}
                      height={260}
                      alt={
                        isRTL
                          ? "تدرب لتقود، تعلم مع إيكوسيف"
                          : "Train to Lead, Learn with ECOSAFE"
                      }
                      className="w-44 sm:w-48 md:w-52 lg:w-56 xl:w-60 h-auto object-contain hover:scale-105 transition-all duration-300 drop-shadow-lg opacity-90 hover:opacity-100"
                    />
                  </div>
                </div>

                {/* Company Description */}
                <div
                  className={`text-center lg:text-left ${
                    isRTL ? "ms-4" : "ms-4"
                  }`}
                >
                  <p
                    className={`text-sm text-gray-300 leading-relaxed font-light ${
                      isRTL ? "text-right" : "text-left"
                    }`}
                  >
                    {isRTL
                      ? "تمكين الأفراد والمؤسسات من خلال التدريب المتخصص والاستشارات المخصصة للقيادة في البيئات عالية المخاطر"
                      : "Empowering individuals and organizations with expert-led training and tailored consultancy for leadership in high-stakes environments"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div
            className={`text-center md:text-left ${
              isRTL ? "md:text-right" : "md:text-left"
            }`}
          >
            <h2 className="text-lg font-bold mb-6 text-white relative">
              {isRTL ? "روابط سريعة" : "Quick Links"}
              <div
                className={`w-6 h-4 flex flex-col gap-1 ${
                  isRTL
                    ? "mx-auto md:mx-0 md:mr-auto md:ml-0"
                    : "mx-auto md:mx-0"
                } mt-2 opacity-60`}
              >
                <div className="h-0.5 bg-ecosafe-green"></div>
                <div className="h-0.5 bg-ecosafe-green"></div>
                <div className="h-0.5 bg-ecosafe-green"></div>
              </div>
            </h2>
            <ul className="space-y-3">
              {[
                {
                  title: isRTL ? "عن الشركة" : "About Us",
                  href: "/about",
                },
                {
                  title: isRTL ? "جميع الدورات" : "All Courses",
                  href: "/training-courses",
                },
                {
                  title: isRTL ? "جميع الفئات" : "All Categories",
                  href: "/sectors",
                },
                {
                  title: isRTL ? "مدننا" : "Our Cities",
                  href: "/cities",
                },
                {
                  title: isRTL ? "تواصل معنا" : "Contact Us",
                  href: "/contact",
                },
              ].map((link) => (
                <li key={link.title}>
                  <Link
                    to={link.href}
                    className={`text-gray-300 hover:text-ecosafe-green transition-all duration-300 text-sm font-medium inline-block ${
                      isRTL ? "hover:-translate-x-1" : "hover:translate-x-1"
                    }`}
                    aria-label={`${isRTL ? "انتقل إلى صفحة" : "Navigate to"} ${
                      link.title
                    }`}
                  >
                    {link.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Categories Section */}
          <div
            className={`text-center md:text-left ${
              isRTL ? "md:text-right" : "md:text-left"
            }`}
          >
            <h2 className="text-lg font-bold mb-6 text-white relative">
              {isRTL ? "الفئات الشائعة" : "Popular Categories"}
              <div
                className={`w-6 h-4 flex flex-col gap-1 ${
                  isRTL
                    ? "mx-auto md:mx-0 md:mr-auto md:ml-0"
                    : "mx-auto md:mx-0"
                } mt-2 opacity-60`}
              >
                <div className="h-0.5 bg-ecosafe-green"></div>
                <div className="h-0.5 bg-ecosafe-green"></div>
                <div className="h-0.5 bg-ecosafe-green"></div>
              </div>
            </h2>

            {/* Loading State */}
            {isLoading && (
              <p className="text-gray-400 text-sm">
                {isRTL ? "جاري تحميل الفئات..." : "Loading categories..."}
              </p>
            )}

            {/* Error State */}
            {error && !isLoading && (
              <p className="text-red-400 text-sm">
                {isRTL ? "فشل في تحميل الفئات" : error}
              </p>
            )}

            {/* Categories List */}
            {!isLoading && categories.length > 0 && (
              <ul className="space-y-3">
                {categories.map((category: Category) => (
                  <li key={category.id}>
                    <Link
                      to={`/courses?categoryId=${
                        category.id
                      }&categoryName=${encodeURIComponent(category.title)}`}
                      className={`text-gray-300 hover:text-ecosafe-green transition-all duration-300 text-sm font-medium inline-block ${
                        isRTL ? "hover:-translate-x-1" : "hover:translate-x-1"
                      }`}
                      aria-label={`${
                        isRTL ? "عرض دورات فئة" : "View courses in"
                      } ${category.title}`}
                    >
                      {category.title}
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {/* No Categories State */}
            {!isLoading && !error && categories.length === 0 && (
              <p className="text-gray-400 text-sm">
                {isRTL ? "لا توجد فئات متاحة" : "No categories available"}
              </p>
            )}
          </div>

          {/* Contact Information */}
          <div
            className={`text-center md:text-left ${
              isRTL ? "md:text-right" : "md:text-left"
            }`}
          >
            <h2 className="text-lg font-bold mb-6 text-white relative">
              {isRTL ? "معلومات التواصل" : "Contact Information"}
              <div
                className={`w-6 h-4 flex flex-col gap-1 ${
                  isRTL
                    ? "mx-auto md:mx-0 md:mr-auto md:ml-0"
                    : "mx-auto md:mx-0"
                } mt-2 opacity-60`}
              >
                <div className="h-0.5 bg-ecosafe-green"></div>
                <div className="h-0.5 bg-ecosafe-green"></div>
                <div className="h-0.5 bg-ecosafe-green"></div>
              </div>
            </h2>
            <div className="space-y-4">
              {/* Location */}
              <div
                className={`flex items-start justify-center md:justify-start ${
                  isRTL ? "flex-row-reverse md:justify-end" : ""
                } gap-3`}
              >
                <MapPin className="h-5 w-5 text-ecosafe-green mt-0.5 flex-shrink-0" />
                <div className={`flex-1 ${isRTL ? "text-right" : "text-left"}`}>
                  <p className="text-sm text-gray-300 font-medium">
                    {isRTL
                      ? "المقر الرئيسي في المملكة العربية السعودية"
                      : "Head Quarter in Saudi Arabia"}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {isRTL
                      ? "مخرج ٣٤، طريق الطائف، الرياض، المملكة العربية السعودية"
                      : "Exit 34, Taif St. Riyadh, KSA"}
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div
                className={`flex items-start justify-center md:justify-start ${
                  isRTL ? "flex-row-reverse md:justify-end" : ""
                } gap-3`}
              >
                <Phone className="h-5 w-5 text-ecosafe-green flex-shrink-0" />
                <div className={`flex-1 ${isRTL ? "text-right" : "text-left"}`}>
                  <a
                    href="tel:+966115062744"
                    className="text-sm text-gray-300 hover:text-ecosafe-green transition-colors font-medium"
                  >
                    {isRTL ? "2744 506 11 966+" : "+966 11 506 2744"}
                  </a>
                </div>
              </div>

              {/* Email */}
              <div
                className={`flex items-start justify-center md:justify-start ${
                  isRTL ? "flex-row-reverse md:justify-end" : ""
                } gap-3`}
              >
                <Mail className="h-5 w-5 text-ecosafe-green flex-shrink-0" />
                <div className={`flex-1 ${isRTL ? "text-right" : "text-left"}`}>
                  <a
                    href="mailto:info@ecosafe.com.sa"
                    className="text-sm text-gray-300 hover:text-ecosafe-green transition-colors font-medium"
                  >
                    info@ecosafe.com.sa
                  </a>
                </div>
              </div>

              {/* Website */}
              <div
                className={`flex items-start justify-center md:justify-start ${
                  isRTL ? "flex-row-reverse md:justify-end" : ""
                } gap-3`}
              >
                <Globe className="h-5 w-5 text-ecosafe-green flex-shrink-0" />
                <div className={`flex-1 ${isRTL ? "text-right" : "text-left"}`}>
                  <a
                    href="https://ecosafe.com.sa/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-300 hover:text-ecosafe-green transition-colors font-medium"
                  >
                    ecosafe.com.sa
                  </a>
                </div>
              </div>
            </div>

            {/* Global Presence */}
            <div className="mt-6 p-4 rounded-lg border border-gray-700 bg-gray-900/50">
              <h3 className="font-semibold mb-2 text-sm text-ecosafe-green">
                {isRTL ? "الحضور العالمي" : "Global Presence"}
              </h3>
              <p className="text-xs text-gray-400">
                {isRTL
                  ? "فروع في: السعودية | مصر | الولايات المتحدة | السويد | الإمارات"
                  : "Branches across: KSA | Egypt | USA | Sweden | UAE"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-700 bg-gray-900/50 backdrop-blur-sm relative z-10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
            <div
              className={`flex flex-col md:flex-row items-center space-y-2 md:space-y-0 text-sm text-gray-400 ${
                isRTL ? "md:space-x-reverse md:space-x-6" : "md:space-x-6"
              }`}
            >
              <p className="font-medium">
                {isRTL
                  ? `© ${new Date().getFullYear()} إيكوسيف للتدريب والاستشارات. جميع الحقوق محفوظة.`
                  : `© ${new Date().getFullYear()} ECOSAFE Training and Consultancy. All rights reserved.`}
              </p>
              <div
                className={`flex ${
                  isRTL ? "space-x-reverse space-x-6" : "space-x-6"
                }`}
              >
                <Link
                  to="/privacy-policy"
                  className="hover:text-ecosafe-green transition-colors font-medium"
                  aria-label={isRTL ? "سياسة الخصوصية" : "Privacy Policy"}
                >
                  {isRTL ? "سياسة الخصوصية" : "Privacy Policy"}
                </Link>

                <Link
                  to="/terms-of-service"
                  className="hover:text-ecosafe-green transition-colors font-medium"
                  aria-label={isRTL ? "شروط الخدمة" : "Terms of Service"}
                >
                  {isRTL ? "شروط الخدمة" : "Terms of Service"}
                </Link>
              </div>
            </div>

            <div
              className={`flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 ${
                isRTL ? "sm:space-x-reverse sm:space-x-3" : "sm:space-x-3"
              }`}
            >
              {/* Company Profile Download Button */}
              <a
                  href={
                    isRTL 
                      ? "https://drive.google.com/uc?export=download&id=1FhrONiLuoIgC0ciA08CAq1NIuuhXZbxd"
                      : "https://drive.google.com/uc?export=download&id=1CecetooFmI1bkgZAy1lAwuxyjW8Dxym9"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  <Button className="px-6 py-2.5 text-white font-semibold text-sm bg-gradient-to-r from-ecosafe-green to-ecosafe-green-dark hover:from-ecosafe-green-dark hover:to-ecosafe-green rounded-lg transition-all duration-200 shadow-lg">
                    <Download className={`h-4 w-4 ${isRTL ? "ml-2" : "mr-2"}`} />
                    {isRTL ? "الملف التعريفي" : "Company Profile"}
                  </Button>
                </a>

                <a
                  href={
                    isRTL 
                      ? "https://drive.google.com/uc?export=download&id=16W-uGg6ZoWgHJ2vDwzoh1Pmgoy7GTeE2"
                      : "https://drive.google.com/uc?export=download&id=1Q6Yc4KT8xTyw0Ue62vir31ps2WTYb4gp"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  <Button className="px-6 py-2.5 text-ecosafe-green font-semibold text-sm border border-ecosafe-green bg-white/10 backdrop-blur-sm hover:bg-ecosafe-green hover:text-white rounded-lg transition-all duration-200">
                    <Calendar className={`h-4 w-4 ${isRTL ? "ml-2" : "mr-2"}`} />
                    {isRTL ? "تقويم ٢٠٢٥" : "2025 Calendar"}
                  </Button>
                </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
